import React, { useEffect, useRef } from 'react';

import styles from './SpaceBackground.module.css';
import { Starfield } from './starfield';

/**
 * SpaceBackground Component
 * Renders an animated starfield background with pink/orange twinkling stars
 * Fully responsive and optimized using RequestAnimationFrame
 */
const SpaceBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const starfieldRef = useRef<Starfield | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Determine star count based on screen size for better mobile performance
    const getStarCount = (): number => {
      const width = window.innerWidth;
      if (width < 768) return 75; // Mobile: 50% fewer stars
      if (width < 1024) return 100; // Tablet: ~30% fewer stars
      return 150; // Desktop: full star count
    };

    // Initialize starfield
    try {
      starfieldRef.current = new Starfield(canvas, getStarCount());
      starfieldRef.current.start();
    } catch (error) {
      console.error('Failed to initialize starfield:', error);
      return;
    }

    // Handle window resize
    const handleResize = () => {
      if (starfieldRef.current) {
        starfieldRef.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      if (starfieldRef.current) {
        starfieldRef.current.destroy();
        starfieldRef.current = null;
      }
    };
  }, []);

  return (
    <div className={styles.spaceBackground}>
      <canvas ref={canvasRef} className={styles.canvas} />
    </div>
  );
};

export default SpaceBackground;
